import { date, z } from "zod";
import AirtableApi, { AirtableRecord } from "../airtable/api";
import { PromiseResult } from "../neverthrow-ext";
import { FetchError, WheeError } from "../error";

import * as BikeVariant from "./variant";
import * as BikeStatus from "./status";
import { firstValueOrNull, transformToShallowObject } from "../zod-utils";
import { formulaGetAllIds } from "../airtable";

import { Temporal } from "@js-temporal/polyfill";
import { ISubscriptionStore, filterBikeSubscriptions } from "../subscription";
import { BikeModelSalesAudienceOptions } from "../bike-model";

export { BikeVariant, BikeStatus }; // Re-exports bike concepts

export interface IBikeStore {
  get(id: string): PromiseResult<Bike, BikeStoreError>;
  getFromFrameNumber(frameNumber: string): PromiseResult<Bike | null, BikeStoreError>;
  preAssignToUser(bikeTypeId: string, userId: string): PromiseResult<Bike, BikeStoreError>;
  getBikeModel(id: string): PromiseResult<BikeModel, BikeStoreError>;
  getBikeModels(id: string[]): PromiseResult<BikeModel[], BikeStoreError>;
}

const ZBikeModel = z
  .object({
    id: z.string(),
    fields: z.object({
      brand: z.string(),
      model: z.string(),
      subscriptions: z.array(z.string()).optional(),
      variant: z
        .enum(["Long", "Short"])
        .transform((val) => (val === "Long" ? ("long" as const) : ("short" as const))),
      bikesAvailableForSale: z.number().default(0),
      comingSoonBikes: z.number().default(0),
      salesAudience: z.enum(BikeModelSalesAudienceOptions).default("Waitlist"),
      availableForPickupFrom: z
        .array(z.nullable(z.string()))
        .default([])
        .transform((dates) => {
          return dates.filter((date) => date !== null);
        }),
      bikeOrderEstimatedDeliveryDates: z
        .array(z.nullable(z.string()))
        .default([])
        .transform((dates) => {
          return dates.filter((date) => date !== null);
        })
    })
  })
  .transform(transformToShallowObject);

export type BikeModel = z.infer<typeof ZBikeModel>;

const ZBike = z
  .object({
    id: z.string(),
    fields: z.object({
      frameNumber: z.string(),
      status: z
        .enum(BikeStatus.getAllATStatuses())
        .default("Available for sale")
        .catch("Available for sale")
        .transform(BikeStatus.fromATData),
      organisationId: z
        .array(z.string())
        .optional()
        .default([])
        .transform((val) => (val.length === 0 ? null : val[0])),
      customerId: z.string().array().default([]).transform(firstValueOrNull)
    })
  })
  .transform(transformToShallowObject)
  .transform((bike) => {
    return {
      ...bike,
      isB2B: bike.organisationId !== null
    };
  });

export type Bike = z.infer<typeof ZBike>;

export type BikeStoreError = BikeModelNotFound | NoBikesAvailable | FetchError;

class NoBikesAvailable extends WheeError {
  public readonly bikeModelId: string;

  constructor(bikeModelId: string) {
    super(`No bikes available for bikeModel: ${bikeModelId}`);
    this.bikeModelId = bikeModelId;
  }
}

class BikeModelNotFound extends WheeError {
  public readonly bikeModelId: string;

  constructor(bikeModelId: string) {
    super(`No bike model for id: ${bikeModelId}`);
    this.bikeModelId = bikeModelId;
  }
}

export class BikeNotFoundError extends WheeError {
  public readonly bikeId: string;

  constructor(bikeId: string) {
    super(`Bike not found: ${bikeId}`);
    this.bikeId = bikeId;
  }

  toResponse(): Response {
    return Response.json(
      {
        error_type: this.constructor.name,
        message: `Could not find bike with id: ${this.bikeId}`
      },
      {
        status: 404
      }
    );
  }
}

export class BikeATStore extends AirtableApi implements IBikeStore {
  // airtable: https://airtable.com/appSjtNqzH36perly/tblN9SkWMmBIXxkJW/viw8Lgx89TZGbEH4s?blocks=hide
  getFromFrameNumber = (frameNumber: string): PromiseResult<Bike | null, BikeStoreError> => {
    const formula = `frameNumber = "${frameNumber}"`;

    return this.getTableRecord({ tableNameOrId: "tblN9SkWMmBIXxkJW", formula }, ZBike);
  };

  preAssignToUser = (bikeTypeId: string, userId: string): PromiseResult<Bike, FetchError> => {
    // Formula for fetching a bike of a given type that can be preassigned to a user.
    // Choose the the bike in the "Bikes available for sales view" that has the earliest pick up date.
    // Bikes available for sales view https://airtable.com/appSjtNqzH36perly/tblN9SkWMmBIXxkJW/viwvB8UYSHXTmyJRM
    return this.getTableRecord(
      {
        tableNameOrId: "bikes",
        view: "viwvB8UYSHXTmyJRM", // Bikes available for sales view
        formula: `bikeTypeId = "${bikeTypeId}"`, // Find matching bike type
        sort: [{ field: "availableForPickupFrom", direction: "asc" }] // Find earliest pick up date
      },
      z.object({ id: z.string() })
    ).andThen((val) => {
      if (val == null) {
        throw new NoBikesAvailable(bikeTypeId);
      }
      const bikeId = val.id;

      const record: AirtableRecord = {
        status: BikeStatus.toATData(BikeStatus.Status.Preassigned),
        preAssignedCustomer: [userId]
      };

      return this.updateRecord("bikes", bikeId, record, ZBike);
    });
  };
  get = (id: string): PromiseResult<Bike, BikeStoreError> => {
    return this.getTableRecordById("bikes", id, ZBike).map((val) => {
      if (!val) {
        throw new BikeNotFoundError(id);
      }
      return val;
    });
  };

  // airtable: https://airtable.com/appSjtNqzH36perly/tblyovDMJ7ZteqUkx/viwXdnXemWqg6xLvC?blocks=hide
  getBikeModel = (id: string): PromiseResult<BikeModel, BikeStoreError> => {
    return this.getTableRecordById("tblyovDMJ7ZteqUkx", id, ZBikeModel).map((bikeType) => {
      if (!bikeType) {
        throw new BikeModelNotFound(id);
      }
      return bikeType;
    });
  };

  // airtable: https://airtable.com/appSjtNqzH36perly/tblyovDMJ7ZteqUkx/viwXdnXemWqg6xLvC?blocks=hide
  getBikeModels = (ids: string[]): PromiseResult<BikeModel[], BikeStoreError> => {
    const formula = formulaGetAllIds(ids);

    return this.getTableRecords("tblyovDMJ7ZteqUkx", formula, ZBikeModel);
  };
}

/**
 *
 * NB: This function will throw.
 * This function is used where both bikeModel data and its associated bike subscriptions are needed.
 *
 * @param bikeStore Class that implements IBikeStore
 * @param subscriptionStore Class that implements ISubscriptionStore
 * @param bikeModelId Id of bikeModel to fetch
 * @returns An object with bikeModel and its associated bike subscriptions in sorted order
 */
export async function getBikeModelAndSubscriptions(
  bikeStore: IBikeStore,
  subscriptionStore: ISubscriptionStore,
  bikeModelId: string
) {
  const bikeModel = await bikeStore.getBikeModel(bikeModelId);

  if (bikeModel.subscriptions === undefined || bikeModel.subscriptions.length === 0) {
    throw new Error("No subscriptions configured for bike model");
  }

  const subscriptions = filterBikeSubscriptions(
    await subscriptionStore.getSubscriptions(bikeModel.subscriptions)
  )
    .filter((sub) => sub.canBePurchasedOnline)
    .sort((a, b) => a.price - b.price);

  return { bikeModel, subscriptions };
}

export type BikeAvailabilityStatus = "available" | "waitlist_only" | "coming_soon" | "sold_out";

export function getBikeModelAvailability(bikeModel: BikeModel): BikeAvailabilityStatus {
  if (bikeModel.bikesAvailableForSale > 0 && bikeModel.salesAudience === "Public") {
    return "available";
  }

  if (bikeModel.bikesAvailableForSale > 0 && bikeModel.salesAudience === "Waitlist") {
    return "waitlist_only";
  }

  if (bikeModel.comingSoonBikes > 0) {
    return "coming_soon";
  }

  return "sold_out";
}
